import { OutlinedSelect, Select, SelectOptionType } from '@octano/global-ui';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  DependencyRule,
  FormField,
  validationSchema,
} from '../validations/validations';

interface DependentSelectProps {
  field: FormField;
  dependency: DependencyRule;
  allOptions: Record<string, SelectOptionType[]>;
  readOnly?: boolean;
}

export default function DependentSelect({
  field,
  dependency,
  allOptions,
  readOnly,
}: DependentSelectProps) {
  const { watch, setValue, control } = useFormContext();
  const parentValue = watch(dependency.parentField);
  const prevParentRef = useRef(parentValue);

  const resolvedParentValue =
    parentValue && typeof parentValue === 'object' && 'value' in parentValue
      ? parentValue.value
      : parentValue;

  const sourceOptions = allOptions[dependency.optionKey] ?? [];

  const filteredOptions = resolvedParentValue
    ? sourceOptions.filter((opt: any) => {
        const parentId = opt.parentId ?? opt.regionId ?? opt.countryId;
        return parentId !== undefined
          ? String(parentId) === String(resolvedParentValue)
          : true;
      })
    : sourceOptions;

  const isDisabled =
    readOnly || (dependency.initiallyDisabled && !resolvedParentValue);

  useEffect(() => {
    const prevResolved =
      prevParentRef.current &&
      typeof prevParentRef.current === 'object' &&
      'value' in prevParentRef.current
        ? prevParentRef.current.value
        : prevParentRef.current;

    if (
      prevResolved !== undefined &&
      prevResolved !== null &&
      String(prevResolved) !== String(resolvedParentValue)
    ) {
      setValue(field.name, null);
    }
    prevParentRef.current = parentValue;
  }, [resolvedParentValue, field.name, setValue, parentValue]);

  const commonProps = {
    control,
    name: field.name,
    label: field.label,
    options: filteredOptions,
    rules: validationSchema(field.validations, {
      isArray: !!field?.properties?.isMulti,
    }),
    placeholder: field?.placeholder,
    disabled: isDisabled,
  };

  if (field?.properties?.outlined) {
    return <OutlinedSelect {...field.properties} {...commonProps} />;
  }

  return <Select {...field.properties} {...commonProps} />;
}
